<template>
    <div class="admin-orders">
        <h1>訂單管理</h1>

        <!-- 篩選區域 -->
        <form class="filters" @submit.prevent="handleSearch">
            <label>
                開始日期:
                <input type="date" v-model="startDate" />
            </label>
            <label>
                結束日期:
                <input type="date" v-model="endDate" />
            </label>
            <label>
                日期範圍:
                <select v-model="selectedRange" @change="handleRangeChange">
                    <option value="">-- 自訂日期 --</option>
                    <option value="today">今天</option>
                    <option value="yesterday">昨天</option>
                    <option value="pre-yesterday">前天</option>
                    <option value="3days">3 日內</option>
                    <option value="15days">15 日內</option>
                    <option value="30days">30 日內</option>
                    <option value="90days">90 日內</option>
                    <option value="180days">180 日內</option>
                    <option value="alldays">顯示全部</option>
                </select>
            </label>
            <label>
                關鍵字:
                <input
                    type="text"
                    placeholder="搜尋訂單ID、姓名、Email..."
                    v-model="keyword"
                    @keyup.enter="handleSearch"
                />
            </label>
            <label>
                訂單狀態:
                <select v-model="status_code">
                    <option value="">-- 全部 --</option>
                    <option
                        v-for="(status, index) in order_status_codes"
                        :key="index"
                        :value="status.status_name"
                    >
                        {{ status.status_label }}
                    </option>
                </select>
            </label>
            <label>
                付款狀態:
                <select v-model="payment_status">
                    <option value="">-- 全部 --</option>
                    <option
                        v-for="(pmStatus, index) in payment_status_methods"
                        :key="index"
                        :value="pmStatus.status_code"
                    >
                        {{ pmStatus.status_label }}
                    </option>
                </select>
            </label>
            <button type="submit">搜尋</button>
        </form>

        <!-- 分頁區域 -->
        <div class="pagination">
            <button :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
                上一頁
            </button>
            <span>第 {{ currentPage }} / {{ totalPages }} 頁</span>
            <button :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
                下一頁
            </button>
        </div>

        交易成功總金額: {{ totalPaidAmount }} 元

        <!-- 讓 table 在手機版可左右滑動 -->
        <div class="table-responsive">
            <table class="orders-table">
                <thead>
                    <tr>
                        <th>操作</th>
                        <th>建立時間</th>
                        <th>訂單狀態</th>
                        <th>訂單ID</th>
                        <th>收貨人姓名</th>
                        <th>付款類型</th>
                        <th>付款狀態</th>
                        <th>總金額</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(order, index) in orders" :key="index">
                        <!-- {{order}} -->
                        <td>
                            <button class="bbtn" @click="viewOrder(order.order_id)">檢視</button>
                            <button class="bbtn" @click="openShippingModal(order)">出貨單</button>
                            <button
                                v-if="order.ship_trade_no"
                                class="bbtn"
                                @click="checkShippingStatus(order)"
                                >
                                物流狀態
                            </button>

                            <button
                                    v-if="order.shipped_files && order.shipped_files.is_called_blackcat === 0"
                                    class="bbtn"
                                    @click="call_cat(order)"
                                >
                                    呼叫黑貓
                                </button>
                                <button
                                    v-if="order.shipped_files && order.shipped_files.is_called_blackcat === 1"
                                    class="bbtn" disabled
                                >
                                    已呼叫黑貓
                                </button>
                        </td>
                        <td>{{ order.created_at }}</td>
                        <td>
                            <span :style="{ color: getStatusColor(order.status_name) }">
                                {{
                                    getOrderStatusData(order.status_name)
                                        ? getOrderStatusData(order.status_name).status_label
                                        : order.status_name
                                }}
                            </span>
                        </td>
                        <td>{{ order.order_id }}</td>
                        <td>{{ order.name }}</td>
                        <td>
                            {{
                                payment_methods.find(
                                (item) => item.method_code === order.payment
                                )?.method_label || '無對應付款方式'
                            }}
                        </td>
                        <td>
                            <span :style="{ color: order.payment_color }">
                                {{ order.payment_status_label }}
                            </span>
                        </td>
                        <td>NT$ {{ order.total_amount }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- 彈窗 Modal: 顯示訂單與明細 -->
    <div class="modal-backdrop" v-if="showOrderModal" @click="closeModal"></div>
    <div class="order-modal" v-if="showOrderModal">
        <h2>訂單詳情</h2>
        <!-- 外層包一個 flex 容器 -->
        <div class="order-details-container">
            <!-- 左欄 -->
            <div class="order-details-left">
                <p>建立時間：{{ currentOrder.created_at }}</p>
                <p>訂單編號：{{ currentOrder.order_id }}</p>
                <p>金流單號：{{ currentOrder.trade_no }}</p>
                <p>物流單號：{{ currentOrder.ship_trade_no }}</p>
                <p>金物流訊息：{{ currentOrder.order_status_msg }}</p>
                <p>
                    訂單狀態：
                    <span
                        v-if="getOrderStatusData(currentOrder.status_name)"
                        :style="{ color: getOrderStatusData(currentOrder.status_name).color }"
                    >
                        {{ getOrderStatusData(currentOrder.status_name).status_label }}
                    </span>
                    <span v-else>
                        {{ currentOrder.status_name }}
                    </span>
                </p>
                <p v-if="currentPaymentStatusData(currentOrder.payment_status)">
                    付款狀態：
                    <span :style="{ color: currentPaymentStatusData(currentOrder.payment_status).color }">
                        {{ currentPaymentStatusData(currentOrder.payment_status).status_label }}
                    </span>
                </p>
                <p v-else>付款狀態：{{ currentOrder.payment_status }}</p>
                <p v-if="currentOrder.paid_at">付款時間：{{ currentOrder.paid_at }}</p>
                <p v-if="currentOrder.payment">
                    付款方式：{{ paymentMethodName }}
                </p>
                <p>運費：NT$ {{ currentOrder.shipping_fee }}</p>
            </div>

            <!-- 右欄 -->
            <div class="order-details-right">
                <p>會員ID：{{ currentOrder.user_id }}</p>
                <p>會員姓名：{{ currentOrder.buy_name }}</p>
                <p>會員手機：{{ currentOrder.buy_phone }}</p>

                <!-- 收貨人姓名：可編輯/顯示 -->
                <p>
                    收貨人姓名：
                    <span v-if="!isEditing">{{ currentOrder.name }}</span>
                    <input
                        v-else
                        type="text"
                        v-model="editedName"
                    />
                </p>

                <!-- 收貨人電話：可編輯/顯示 -->
                <p>
                    收貨人電話：
                    <span v-if="!isEditing">{{ currentOrder.phone }}</span>
                    <input
                        v-else
                        type="text"
                        v-model="editedPhone"
                    />
                </p>

                <!-- 收貨人地址：可編輯/顯示 -->
                <p>
                    收貨人住址：
                    <span v-if="!isEditing">
                        <span v-if="currentOrder.zip">
                            ({{ currentOrder.zip }}) {{ currentOrder.city }}{{ currentOrder.district }}{{ currentOrder.street }}
                        </span>
                    </span>
                    <template v-else>
                        <input
                            type="text"
                            v-model="editedZip"
                            placeholder="郵遞區號"
                            style="width:70px; margin-right:5px;"
                        />
                        <input
                            type="text"
                            v-model="editedCity"
                            placeholder="縣市"
                            style="width:70px; margin-right:5px;"
                        />
                        <input
                            type="text"
                            v-model="editedDistrict"
                            placeholder="區"
                            style="width:70px; margin-right:5px;"
                        />
                        <input
                            type="text"
                            v-model="editedStreet"
                            placeholder="街道"
                            style="width:150px;"
                        />
                    </template>
                    <span v-if="currentOrder.store_id">
                        ({{ currentOrder.store_id }})[
                        <span style="color:darkgreen">{{currentOrder.store_name}}</span>
                        ]{{currentOrder.store_addr}}
                    </span>
                </p>

                <p>Email：{{ currentOrder.buy_email }}</p>
                <p v-if="currentOrder.shipping">運送方式：{{ shippingMethodName }}</p>
                <p><span v-if="currentOrder.invoice_type === 'personal'">個人發票：<span v-if="currentOrder.carrier_type === '3J0002'">手機條碼
                    {{ currentOrder.mobile_barcode }}</span>
                  <span v-else-if="currentOrder.carrier_type === 'CQ0001'">自然人憑證 {{ currentOrder.citizen_barcode }}</span>
                  <span v-else-if="currentOrder.carrier_type === 'amego'">會員載具 {{ currentOrder.buy_email }}</span>
                </span>
                <span v-else-if="currentOrder.invoice_type === 'Company'">公司發票：{{ currentOrder.company_name }} - {{ currentOrder.tax_id
                  }}</span>
                <span v-else-if="currentOrder.invoice_type === 'Donate'">發票捐贈：{{currentOrder.donate_code}}</span>
              </p>
              <p v-if="currentOrder.invoice_no">發票號碼：{{ currentOrder.invoice_no }}</p>
            </div>
        </div>

        <!-- 最下方單獨顯示「總金額」 -->
        <p class="order-total-amount">
            總金額：NT$ {{ currentOrder.total_amount }}
        </p>

        <h3>訂單明細</h3>
        <table>
            <thead>
                <tr>
                    <th>商品名稱</th>
                    <th>規格</th>
                    <th>數量</th>
                    <th>單價</th>
                    <th>小計</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, idx) in orderDetails" :key="idx" :class="{ 'gift-row': item.is_gift }">
                    <td>
                        <img
                            v-if="item.image_url"
                            :src="item.image_url"
                            class="product-img"
                        />
                        <span v-if="item.is_gift" class="gift-badge">🎁 贈品</span>
                        {{ item.title }}
                    </td>
                    <td>{{ item.variant_name || '—' }}</td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ item.is_gift ? '贈送' : item.price }}</td>
                    <td>{{ item.is_gift ? '—' : item.quantity * item.price }}</td>
                </tr>
                <tr v-if="currentOrder.discount_total > 0" class="discount-row">
                    <td colspan="4" style="text-align:right; color:#c0392b; font-weight:600;">折扣優惠</td>
                    <td style="color:#c0392b; font-weight:600;">-{{ currentOrder.discount_total }}</td>
                </tr>
                <tr class="subtotal-row">
                    <td colspan="4" style="text-align:right; font-weight:700;">小計</td>
                    <td style="font-weight:700;">{{ currentOrder.total_amount }}</td>
                </tr>
            </tbody>
        </table>

        <!-- 編輯／保存 按鈕 & 關閉按鈕 -->
        <div class="modal-footer">
            <button
                v-if="!showOrderEditButton"
                class="close-btn"
                @click="closeModal"
            >
                關閉
            </button>

            <!-- 「關閉」左邊多一個「編輯」按鈕，點擊後若 isEditing=false 則進入編輯狀態；若 true 則執行保存 -->
            <button
                v-if="showOrderEditButton"
                class="close-btn"
                @click="toggleEdit"
            >
                {{ isEditing ? '保存' : '編輯' }}
            </button>
            <button
                class="close-btn"
                @click="closeModal"
            >
                關閉
            </button>
        </div>
    </div>

    <!-- 出貨單 Modal -->
    <div class="modal-backdrop" v-if="showShippingModal" @click="closeShippingModal"></div>
    <div class="shipping-modal" v-if="showShippingModal">
        <h2>出貨單設定</h2>

        <div class="form-group">
            <label>運送方式</label>
            <div class="readonly-text">{{ shippingModalData?.shippingLabel }}</div>
        </div>

        <div class="form-group">
            <label>GoodsType</label>
            <select v-model="shippingForm.goodsType">
                <option value="1">常溫</option>
                <option value="2">冷凍</option>
                <option value="3">冷藏</option>
            </select>
        </div>

        <div class="form-group">
            <label>出貨日期 (ShipDate)</label>
            <input type="date" v-model="shippingForm.shipDate" />
        </div>

        <div v-if="shippingModalData?.isHomeDelivery">
            <div class="form-group">
                <label>希望配達日期 (DeliveryDate)</label>
                <input type="date" v-model="shippingForm.deliveryDate" />
            </div>

            <div class="form-group">
                <label>包裹尺寸 (Spec)</label>
                <select v-model="shippingForm.spec">
                    <option value="1">60 公分</option>
                    <option value="2">90 公分</option>
                    <option value="3">120 公分</option>
                    <option value="4">150 公分 (僅常溫)</option>
                </select>
            </div>

            <div class="form-group">
                <label>Memo (備註給黑貓)</label>
                <textarea v-model="shippingForm.memo" rows="3"></textarea>
            </div>
        </div>

        <div class="modal-footer">
            <button
            @click="confirmShipping"
            class="confirm-btn"
            :disabled="isSubmitting"
            >
            <span v-if="isSubmitting">處理中...</span>
            <span v-else>建立</span>
            </button>
            <button @click="closeShippingModal" class="close-btn">取消</button>
        </div>
    </div>

    <!-- 物流狀態 Modal 遮罩 -->
    <div
    class="modal-backdrop"
    v-if="showShippingStatusModal"
    @click="closeShippingStatusModal"
    ></div>

    <!-- 物流狀態 Modal -->
    <div class="shipping-status-modal" v-if="showShippingStatusModal">
    <h2>物流狀態</h2>

  
    <div class="logistics-info" v-if="shippingStatusData">
    <!-- 你可以先把整個物件印出來看看 -->
   <!-- {{ shippingStatusData }} -->

    <!-- 根據後端回傳的欄位來顯示 -->
    <p>物流單號：{{ shippingStatusData.ShipTradeNo }}</p>
    <p>配送狀態： <span :style="{ color: shippingStatusData.orderStatusCode.color }">{{shippingStatusData.orderStatusCode.status_label}}</span> ( {{ shippingStatusData.orderStatusCode.status_name }} :  {{ shippingStatusData.ShipStatus }}) 
    </p>
    <p>狀態說明：
      {{ shippingStatusData.ShipStatusDesc }}
    </p>
    <p>收貨人：{{ shippingStatusData.Consignee }} / {{ shippingStatusData.ConsigneeMobile }}</p>
    <p>更新時間：{{ shippingStatusData.ShipStatusTime }}</p>
    <p>寄送方式：{{ shippingStatusData.LgsType }}</p>
    <p>門市：{{ shippingStatusData.StoreName }} ({{ shippingStatusData.StoreID }})</p>

    <!-- 若未來需要多筆歷程，可用 v-for -->
    <!--
    <h3>歷程：</h3>
    <ul>
      <li v-for="(step, idx) in shippingStatusData.history" :key="idx">
        {{ step.date }} - {{ step.status }} / {{ step.location }}
      </li>
    </ul>
    -->
  </div>
    <div v-else>
        <p>查詢中或尚無資料</p>
    </div>

    <div class="modal-footer">
        <button class="close-btn" @click="closeShippingStatusModal">
        關閉
        </button>
    </div>
    </div>



</template>

<script>
import axios from 'axios'
import { useAdminSysTempStore } from '@/store/adminSysTempStore.js'
import Swal from 'sweetalert2'

export default {
    name: 'AdminOrdersPage',
    data() {
        // 在此上方可定義或在 methods 中定義一個:
        function formatTaiwanDate(d) {
        const str = d.toLocaleString("sv-SE", { timeZone: "Asia/Taipei" })
        return str.slice(0, 10) // "YYYY-MM-DD"
        }
        const now = new Date()
        const day30Before = new Date(now.getTime() + 8 - 30 * 24 * 60 * 60 * 1000)
       // const formatDate = (date) => date.toISOString().split('T')[0]

        return {
            isSubmitting: false, 
            orders: [],
            limit: 10,
            offset: 0,
            currentPage: 1,
            totalPages: 0,
            total: 0,
            totalPaidAmount: 0,

            currentOrder: {},
            orderDetails: [],
            startDate: formatTaiwanDate(day30Before),
            endDate: formatTaiwanDate(now),
            keyword: '',
            status_code: '',
            payment_status: '',
            selectedRange: '15days',

            showOrderModal: false,       // 是否顯示「訂單詳情」彈窗
            showOrderEditButton: true,   // 是否顯示「編輯」按鈕 (可根據需求自訂控制)

            // 是否正在編輯
            isEditing: false,

            // 用來暫存可編輯欄位
            editedName: '',
            editedPhone: '',
            editedZip: '',
            editedCity: '',
            editedDistrict: '',
            editedStreet: '',

            payment_status_methods: [],
            payment_methods: [],
            shipping_methods: [],
            order_status_codes: [],

            // 物流狀態
            showShippingStatusModal: false,
            shippingStatusData: null,
            // 出貨單相關
            showShippingModal: false,     // 是否顯示「出貨單 Modal」
            shippingModalData: null,      // 這裡放「當前要出貨的」訂單資訊 or 運送方式
            shippingForm: {              // 要填給後端的參數
                order_id: null,
                goodsType: '1',           // 1=常溫,2=冷凍,3=冷藏 (預設常溫)
                shipDate: '',             // 出貨日期
                deliveryDate: '',         // 希望配達日期 (僅宅配用)
                spec: '1',                // 預設 60
                memo: ''                  // 備註
            },
        }
    },
    
    async beforeCreate() {
        // 檢查是否登入
        const useAdminSysTemp = useAdminSysTempStore()
        await useAdminSysTemp.fetchAdminCheckLogin()
        if (!useAdminSysTemp.isLoggedIn) {
            this.$router.push('/admin/login')
        }
    },
    async created() {
        const useAdminSysTemp = useAdminSysTempStore()
        await useAdminSysTemp.fetchAdminSysTempData()
        this.payment_status_methods = useAdminSysTemp.payment_status_methods
        this.payment_methods = useAdminSysTemp.payment_methods
        this.shipping_methods = useAdminSysTemp.shipping_methods
        this.order_status_codes = useAdminSysTemp.order_status_codes

        // 初始化時，預設選擇 "3days"
        this.handleRangeChange()
        await this.fetchOrders()
    },
    mounted() {
        // 監聽 Esc 鍵
        window.addEventListener('keydown', this.handleEscape)
    },
    beforeUnmount() {
        // 離開元件時移除監聽，避免記憶體洩漏
        window.removeEventListener('keydown', this.handleEscape)
    },
    watch: {
        // 當 showOrderModal 或 showShippingModal 為 true → body overflow hidden；false → 恢復
        showOrderModal(val) {
            if (val || this.showShippingModal) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = ''
            }
        },
        showShippingModal(val) {
            if (val || this.showOrderModal) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = ''
            }
        }
    },
    computed: {
        shippingMethodName() {
            const found = this.shipping_methods.find(
                (item) => item.id === this.currentOrder.shipping
            )
            return found ? found.name : '無對應運送方式'
        },
        paymentMethodName() {
            const found = this.payment_methods.find(
                (item) => item.method_code === this.currentOrder.payment
            )
            return found ? found.method_label : '無對應付款方式'
        }
    },
    methods: {
        formatTaiwanDate(d) {
        const str = d.toLocaleString("sv-SE", { timeZone: "Asia/Taipei" })
        return str.slice(0, 10) // "YYYY-MM-DD"
        },
        handleEscape(e) {
            if (e.key === 'Escape') {
                // 如果 Modal 正在顯示，就關閉
                if (this.showOrderModal) {
                    this.closeModal()
                }
                if (this.showShippingModal) {
                    this.closeShippingModal()
                }
            }
        },
        // 撈訂單
        async fetchOrders() {
            try {
                const go_startDate = this.startDate + ' 00:00:00'
                const go_endDate = this.endDate + ' 23:59:59'
                const response = await axios.post('/api/admin/getOrdersByPage', {
                    limit: this.limit,
                    offset: this.offset,
                    startDate: go_startDate,
                    endDate: go_endDate,
                    keyword: this.keyword,
                    status_code: this.status_code,
                    payment_status: this.payment_status
                })

                if (response.data.success) {
                    this.orders = response.data.orders
                    this.total = response.data.total
                    this.totalPaidAmount = response.data.totalPaidAmount
                    this.totalPages = Math.ceil(this.total / this.limit)
                } else {
                    console.error('取得訂單失敗:', response.data.message)
                }
            } catch (error) {
                console.error('取得訂單錯誤:', error)
            }
        },
        // 搜尋
        handleSearch() {
            this.currentPage = 1
            this.offset = 0
            this.fetchOrders()
        },
        // 分頁
        goToPage(page) {
            if (page < 1) return
            if (page > this.totalPages) return

            this.currentPage = page
            this.offset = (page - 1) * this.limit
            this.fetchOrders()
        },
        // 查看訂單 & 取得詳細明細
        async viewOrder(orderId) {
            try {
                const res = await axios.post('/api/admin/getAdminOrderDetails', {
                    order_id: orderId
                })

                if (res.data.success) {
                    this.showOrderModal = true
                    // 找到當前訂單
                    this.currentOrder = this.orders.find((order) => order.order_id === orderId)
                    // 轉為數字型態（避免帶入字串）
                    this.currentOrder.shipping = Number(this.currentOrder.shipping) || 0
                    this.currentOrder.payment_status = Number(this.currentOrder.payment_status) || 0

                    // 訂單明細
                    this.orderDetails = res.data.orderDetails || []

                    // 進入查看時，預設為非編輯模式
                    this.isEditing = false
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: '取得訂單明細失敗！',
                        showConfirmButton: false,
                        timer: 1500
                    })
                }
            } catch (error) {
                console.error('取得訂單明細錯誤:', error)
            }
        },
          // 查詢物流狀態
        async checkShippingStatus(order) {
            try {
            // 假設後端 API 為 /api/admin/getShippingStatus
            // 你可以根據實際需求，傳遞 ship_trade_no 或其它參數
          // 2. 根據 shipping (1,2,3=7-11; 4,5=宅配...etc) 判斷是超商 or 宅配
            const response = await axios.post('/api/admin/getShippingStatus', {
                ship_trade_no: order.ship_trade_no,
                shipping : Number(order.shipping),
            })
            if (response.data.success) {
                const result = response.data.result

                console.log(result)
                // 1) **更新前端**該筆訂單的狀態 (假設後端有傳回 result.status_name)
                order.status_name = result.orderStatusCode && result.orderStatusCode.status_name || order.status_name
                // 拿到物流狀態資料，存到 shippingStatusData
                this.shippingStatusData = result
                //console.log(this.shippingStatusData)
                // 顯示 Modal
                this.showShippingStatusModal = true
            } else {
                Swal.fire({
                icon: 'error',
                title: '查詢物流狀態失敗',
                text: response.data.message || '請稍後再試'
                })
            }
            } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: '系統錯誤',
                text: '查詢物流狀態發生錯誤，請稍後再試'
            })
            }
        },

        // 關閉物流狀態 Modal
        closeShippingStatusModal() {
            this.showShippingStatusModal = false
            this.shippingStatusData = null
        },
        // 日期範圍切換
        handleRangeChange() {
            const now = new Date()
           // const formatDate = (date) => date.toISOString().split('T')[0]
            let chosenDate
            switch (this.selectedRange) {
                case 'today':
                    this.startDate = this.formatTaiwanDate(now)
                    this.endDate = this.formatTaiwanDate(now)
                    break
                case 'yesterday':
                    chosenDate = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(chosenDate)
                    break
                case 'pre-yesterday':
                    chosenDate = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(chosenDate)
                    break
                case '3days':
                    chosenDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(now)
                    break
                case '15days':
                    chosenDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(now)
                    break                 
                case '30days':
                    chosenDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(now)
                    break
                case '90days':
                    chosenDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(now)
                    break
                case '180days':
                    chosenDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000)
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(now)
                    break
                case 'alldays':
                    chosenDate = new Date('2000-01-01')
                    this.startDate = this.formatTaiwanDate(chosenDate)
                    this.endDate = this.formatTaiwanDate(now)
                    break
                default:
                    // 自訂日期，不額外處理
                    break
            }
        },
        currentPaymentStatusData(status_code) {
            return this.payment_status_methods.find(
                (item) => item.status_code === status_code
            )
        },
        getOrderStatusData(statusName) {
            return this.order_status_codes.find(
                (item) => item.status_name === statusName
            )
        },
        // 根據 status_name 找到對應的 color
        getStatusColor(statusName) {
            const found = this.order_status_codes.find(
                (item) => item.status_name === statusName
            )
            return found ? found.color : '#000'
        },
        // 關閉「訂單詳情」彈窗
        closeModal() {
            this.showOrderModal = false
        },

        // 編輯 / 保存
        async toggleEdit() {
            // 如果目前是「編輯狀態」，按鈕按下後要「保存」
            if (this.isEditing) {
                // 呼叫後端 API，更新收貨人資訊
                try {
                    const response = await axios.post('/api/admin/updateOrderReceiver', {
                        order_id: this.currentOrder.order_id,
                        name: this.editedName,
                        phone: this.editedPhone,
                        zip: this.editedZip,
                        city: this.editedCity,
                        district: this.editedDistrict,
                        street: this.editedStreet
                    })
                    if (response.data.success) {
                        // 更新成功後，將 currentOrder 資料改成最新
                        this.currentOrder.name = this.editedName
                        this.currentOrder.phone = this.editedPhone
                        this.currentOrder.zip = this.editedZip
                        this.currentOrder.city = this.editedCity
                        this.currentOrder.district = this.editedDistrict
                        this.currentOrder.street = this.editedStreet

                        Swal.fire({
                            icon: 'success',
                            title: '更新成功！',
                            showConfirmButton: false,
                            timer: 1500
                        })

                        // 返回「編輯」按鈕狀態
                        this.isEditing = false
                    } else {
                        console.error('更新失敗：', response.data.message)
                        Swal.fire({
                            icon: 'error',
                            title: '更新失敗！',
                            showConfirmButton: false,
                            timer: 1500
                        })
                    }
                } catch (err) {
                    console.error(err)
                    Swal.fire({
                        icon: 'error',
                        title: '更新失敗！',
                        showConfirmButton: false,
                        timer: 1500
                    })
                }
            } else {
                // 目前是「瀏覽狀態」，按下「編輯」，進入編輯模式
                // 初始化編輯欄位
                this.editedName = this.currentOrder.name
                this.editedPhone = this.currentOrder.phone
                this.editedZip = this.currentOrder.zip
                this.editedCity = this.currentOrder.city
                this.editedDistrict = this.currentOrder.district
                this.editedStreet = this.currentOrder.street

                this.isEditing = true
            }
        },

        // ========== 出貨單相關 ==========

        // 關閉出貨單彈窗
        closeShippingModal() {
            this.showShippingModal = false
        },
        call_cat(order){
            //件數太少暫時不開放
            console.log(order)
            Swal.fire({
                icon: 'error',
                title: '呼叫黑貓失敗！',
                text: '目前尚未開放呼叫黑貓功能，件數少於1-2件。'
            }) 
        },
        // 點擊「出貨單」按鈕
        openShippingModal(order) {
            // 1. 先清空 shippingForm
            this.shippingForm = {
                order_id: order.order_id,
                ship_trade_no : order.ship_trade_no,
                shipType : 1,
                goodsType: '1',
                shipDate: '',
                deliveryDate: '',
                spec: undefined,
                lgsType : 'C2C' , // 店到店
                memo: undefined,
                HideProdDesc : undefined,
                labelMode: 2 // 1=A4版型 2=直立式
            }

            // 尚未付款 且 也沒有物流單 → 擋住
            // 注意：信用卡已付款（payment_status=1）的訂單即使暫時沒有物流單，也允許繼續（後端會補建立）
            if (!this.shippingForm.ship_trade_no && Number(order.payment_status) !== 1) {
                Swal.fire({
                    icon: 'error',
                    title: '警告！',
                    text: '訂單尚未付款，無法建立出貨單。'
                })
                return
            }

            // 2. 根據 shipping (1,2,3=7-11; 4,5=宅配...etc) 判斷是超商 or 宅配
            const shippingVal = Number(order.shipping)
            let shippingLabel = ''
            let isHomeDelivery = false

            switch(shippingVal) {
                case 1: shippingLabel = '7-11'; break
                case 2: shippingLabel = '全家'; break
                case 3: shippingLabel = '萊爾富'; break
                case 4: shippingLabel = '宅配到家'; isHomeDelivery = true; break
                case 5: shippingLabel = '離島宅配'; isHomeDelivery = true; break
                default:
                    shippingLabel = '未知'
            }
            
            // 3. 設定出貨日期
            //   - 若是超商 → 預設今天
            //   - 若是宅配 → 預設明天
            const now = new Date()
            if (isHomeDelivery) {
                now.setDate(now.getDate() + 1) // +1天
            }
            this.shippingForm.shipDate = this.formatToTaiwanDate(now)
            
            // 4. 若是宅配，再多設定 DeliveryDate = +3天
            if (isHomeDelivery) {
                const deliverDate = new Date()
                deliverDate.setDate(deliverDate.getDate() + 3) // +3天
                this.shippingForm.deliveryDate = this.formatToTaiwanDate(deliverDate)
            }

            if(isHomeDelivery){
                this.shippingForm.lgsType = "HOME";
                this.shippingForm.shipType = 2;
                this.shippingForm.spec = "1";
                this.shippingForm.memo = "";
                this.shippingForm.labelMode = undefined;
                this.shippingForm.HideProdDesc = "Y";
            }
           
            // 5. 存 modalData
            this.shippingModalData = {
                shippingLabel,
                isHomeDelivery
            }

            // 6. 顯示 Modal
            this.showShippingModal = true
        },
        // 用於將 JS Date 轉成 "YYYY-MM-DD" (台灣時區) 的小工具
        formatToTaiwanDate(jsDate) {
            // 先 clone 一份，以免影響原 Date
            const date = new Date(jsDate.getTime())
            // 要得到台灣時區的 YYYY-MM-DD，可以用toLocaleString再截前10碼
            const taiwanStr = date.toLocaleString('sv-SE', {
                timeZone: 'Asia/Taipei'
            }) // e.g. "2025-01-27 12:34:56"
            return taiwanStr.slice(0, 10) // "2025-01-27"
        },
        // 確認「建立」出貨單
        async confirmShipping() {
            this.isSubmitting = true;
            try {
                const { data } = await axios.post('/api/admin/createShippingOrder', {
                    order_id: this.shippingForm.order_id,
                    ship_trade_no : this.shippingForm.ship_trade_no,
                    lgsType : this.shippingForm.lgsType,
                    shipType : this.shippingForm.shipType,
                    goodsType: this.shippingForm.goodsType,
                    shipDate: this.shippingForm.shipDate,
                    deliveryDate: this.shippingForm.deliveryDate,
                    spec: this.shippingForm.spec,
                    hideProdDesc: this.shippingForm.hideProdDesc,
                    labelMode : this.shippingForm.labelMode,
                    memo: this.shippingForm.memo
                })
                if (data.success) {
                    if (data.result && typeof data.result === 'string') {
                        if (data.result.includes('HOME01092')) {
                        // 代表該單已重複領取
                        Swal.fire({
                            icon: 'error',
                            title: '已重複領取',
                            text: '該宅配單已處理過，無法重複領取。若需要再次列印，請點「下載PDF」。'
                        })
                        return
                    }
                        // 2) 一般情況 → 新視窗載入 payData
                        const newWin = window.open('', '_blank');
                        newWin.document.write(data.result);
                        newWin.document.close();
                    }
                    Swal.fire({
                        icon: 'success',
                        title: '出貨單已建立！',
                        text: data.message || '',
                        timer: 2000
                    })
                    this.showShippingModal = false
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: '出貨單建立失敗',
                        text: data.message || '請稍後再試'
                    })
                }
            } catch (error) {
                console.error(error)
                Swal.fire({
                    icon: 'error',
                    title: '系統錯誤',
                    text: '建立出貨單發生錯誤，請稍後再試'
                })
            } finally {
                // 無論成功或失敗都要解鎖按鈕
                this.isSubmitting = false;
            }
        }
    }
}
</script>

<style scoped>
.admin-orders {
    background-color: #fafafa;
    padding: 1rem;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 篩選表單 */
.filters {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: flex-end;
}

.filters label {
    display: flex;
    flex-direction: column;
    font-size: 16px;
}

.filters button {
    background-color: #2e3a59;
    color: #fff;
    border: none;
    padding: 0.4rem 1rem;
    cursor: pointer;
    border-radius: 4px;
    font-size: 16px;
    margin-top: auto;
}

.filters button:hover {
    background-color: #3b4b6b;
}

/* 分頁區域 */
.pagination {
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: center;
    margin-bottom: 1.5rem;
}

.pagination button {
    background-color: #2e3a59;
    color: #fff;
    border: none;
    padding: 0.4rem 1rem;
    cursor: pointer;
    border-radius: 4px;
    font-size: 16px;
}

.table-responsive {
    position: relative;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.orders-table {
    min-width: 1200px;
    border-collapse: collapse;
    margin-bottom: 1rem;
    width: 100%;
}

.orders-table th,
.orders-table td {
    border: 1px solid #ccc;
    padding: 0.5rem 0.75rem;
    text-align: center;
    font-size: 18px;
}

.orders-table select {
    font-size: 1.2rem;
    padding: 0.4rem 0.6rem;
}

.filters select {
    font-size: 1.2rem;
    padding: 0.4rem 0.6rem;
}

/* -- 通用背景遮罩 -- */
.modal-backdrop {
    position: fixed;
    top: 0; 
    left: 0; 
    width: 100%; 
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
}

/* -- 訂單詳情 Modal -- */
.order-modal {
    position: fixed;
    top: 50%; 
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    width: 800px;
    max-width: 90%;
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    z-index: 999;

    /* 讓內容可在 Modal 裡面滾動，而背景固定不動 */
    max-height: 80vh;
    overflow-y: auto;
}
.order-modal h2 {
    margin-top: 0;
}
.order-modal table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}
.order-modal table th,
.order-modal table td {
    border: 1px solid #ccc;
    padding: 0.5rem;
    text-align: center;
}
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.5rem;
}
.close-btn {
    background-color: #2e3a59;
    color: #fff;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
}
.close-btn:hover {
    background-color: #3b4b6b;
}
.bbtn, .btn-viewOrder {
    background-color: #2e3a59;
    color: #fff;
    border: none;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
}
.bbtn {
    margin-right: 8px; /* 為每個按鈕加上右側 8px 間隔 */
}
.bbtn:hover,
.btn-viewOrder:hover {
    background-color: #3b4b6b;
}

/* 父容器為彈性布局，左右並排 */
.order-details-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
}

.order-details-left,
.order-details-right {
    flex: 1 1 45%;
}

.order-total-amount {
    font-weight: bold;
    font-size: 1.2rem;
    margin-top: 1rem;
}
.product-img {
    width: 40px;
    height: auto;
    margin-right: 8px;
    object-fit: cover;
}
.gift-row { background: #f0faf4; }
.discount-row td { background: #fff8f8; }
.subtotal-row td { background: #f5f5f5; border-top: 2px solid #ccc; }
.gift-badge {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 20px;
    background: #27ae60;
    color: #fff;
    margin-right: 5px;
    vertical-align: middle;
}

/* -- 出貨單 Modal -- */
.shipping-modal {
    position: fixed;
    top: 50%; 
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    width: 500px;
    max-width: 90%;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    box-shadow: 0 3px 10px rgba(0,0,0,0.3);
    z-index: 999;

    max-height: 80vh;
    overflow-y: auto;
}
.shipping-modal h2 {
    margin-top: 0;
    margin-bottom: 1rem;
}
.form-group {
    margin-bottom: 1rem;
}
.form-group label {
    display: block;
    font-weight: bold;
    margin-bottom: 0.4rem;
}
.form-group select,
.form-group input[type="date"],
.form-group textarea {
    width: 100%;
    padding: 0.4rem;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
}
.readonly-text {
    padding: 0.4rem;
    font-size: 1rem;
    background: #f9f9f9;
    border: 1px solid #ccc;
    border-radius: 4px;
}
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}
.confirm-btn {
    background-color: #28a745;
    color: #fff;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
}
.confirm-btn:hover {
    background-color: #218838;
}

.shipping-status-modal {
  position: fixed;
  top: 50%; 
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  width: 500px;
  max-width: 90%;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.3);
  z-index: 999;

  max-height: 80vh;
  overflow-y: auto;
}


/* 手機 RWD */
@media (max-width: 768px) {
    .table-responsive {
        max-width: 310px;
    }
    .filters {
        flex-direction: column;
        align-items: flex-start;
    }
    .orders-table th,
    .orders-table td {
        font-size: 14px;
        padding: 0.4rem;
    }
    .pagination {
        justify-content: start;
    }
    .order-details-container {
        flex-direction: column;
    }
    .order-details-left,
    .order-details-right {
        width: 100%;
    }
    .shipping-modal {
        width: 90%;
        max-width: 90%;
    }
    .order-modal {
        width: 90%;
        max-width: 90%;
    }
}
</style>
